import React, { useState } from "react";
import {
  LayoutGrid,
  Map as MapIcon,
  Users,
  BarChart3,
  Building,
  Home,
  TrendingUp,
  Search,
  Filter,
  DollarSign,
  Mountain,
  Waves,
  Briefcase,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Globe,
  Gem,
  Plane,
  Zap,
  Megaphone,
  ChevronLeft,
  Target,
  FileText,
  ShieldAlert,
  ArrowUpRight,
} from "lucide-react";

// --- THEME & STYLES ---
const palette = {
  bg: "#F9FAFB", // Gray 50
  bgSidebar: "#FFFFFF",
  glass: "rgba(255, 255, 255, 0.95)", // Increased opacity for readability
  glassBorder: "rgba(0, 0, 0, 0.06)",
  text: "#111827", // Gray 900
  textSoft: "#4B5563", // Gray 600
  textFaint: "#9CA3AF", // Gray 400
  brand: "#C7A965", // Dark Gold
  brandGlow: "rgba(199, 169, 101, 0.15)",
  accent: "#E3D6B3", // Light Gold/Cream
  success: "#059669", // Emerald 600
  warning: "#D97706", // Amber 600
  danger: "#DC2626", // Red 600
  purple: "#7C3AED", // Violet 600
};

const commonStyles = {
  container: {
    display: "flex",
    height: "100vh",
    width: "100vw",
    backgroundColor: palette.bg,
    color: palette.text,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    overflow: "hidden",
  },
  sidebar: {
    width: "280px",
    backgroundColor: palette.bgSidebar,
    borderRight: `1px solid ${palette.glassBorder}`,
    display: "flex",
    flexDirection: "column",
    padding: "24px",
    zIndex: 10,
    boxShadow: "1px 0 10px rgba(0,0,0,0.02)",
  },
  navItem: (active) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: active ? palette.brandGlow : "transparent",
    color: active ? "#947631" : palette.textSoft,
    border: active ? `1px solid ${palette.brand}40` : "1px solid transparent",
    transition: "all 0.2s ease",
    fontWeight: active ? 600 : 500,
    fontSize: "14px",
  }),
  glassPanel: {
    backgroundColor: palette.glass,
    backdropFilter: "blur(20px)",
    border: `1px solid ${palette.glassBorder}`,
    borderRadius: "16px",
    padding: "32px",
    height: "100%",
    overflowY: "auto",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
  },
  card: {
    backgroundColor: "#FFFFFF",
    border: `1px solid ${palette.glassBorder}`,
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  h2: {
    fontSize: "18px",
    fontWeight: "700",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: palette.text,
    letterSpacing: "-0.01em",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: "24px",
  },
  badge: (tone = "default") => {
    const map = {
      default: { bg: "#F3F4F6", text: "#374151" },
      brand: { bg: "#FFFBEB", text: "#92400E" },
      success: { bg: "#ECFDF5", text: "#047857" },
      warning: { bg: "#FFF7ED", text: "#B45309" },
      danger: { bg: "#FEF2F2", text: "#B91C1C" },
      purple: { bg: "#F5F3FF", text: "#6D28D9" },
    };
    const style = map[tone] || map.default;
    return {
      padding: "4px 10px",
      borderRadius: "6px",
      fontSize: "11px",
      fontWeight: "600",
      backgroundColor: style.bg,
      color: style.text,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      whiteSpace: "nowrap",
    };
  },
};

// --- DATA INTELLIGENCE LAYER ---

const PROPERTIES = [
  {
    id: "P-101",
    title: "Porto Montenegro Regent Penthouse",
    city: "Tivat",
    sub: "Porto Montenegro",
    price: 2800000,
    size: 240,
    type: "Apartment",
    status: "Resale",
    tags: ["waterfront", "marina"],
    performance: {
      yield_realized: 4.2,
      occupancy: 85,
      opex_annual: 45000,
      rent_high: 1200,
      rent_low: 400,
    },
    vendor: {
      type: "Private",
      urgency: "Low",
      exclusivity: "Exclusive",
      motivation: "Divestment",
    },
    meta: {
      project: "Regent",
      floor: 5,
      view: "Sea (First Line)",
      dist_sea: 10,
      legal: "Clean",
    },
    fit_score: { "The Villa Collector": 98, "Lifestyle Expat": 65 },
    days_on_market: 145,
  },
  {
    id: "P-102",
    title: "Becici Horizon Complex",
    city: "Budva",
    sub: "Becici",
    price: 185000,
    size: 62,
    type: "Apartment",
    status: "New Construction",
    tags: ["sea view", "rental yield"],
    performance: {
      yield_realized: null,
      yield_est: 7.8,
      occupancy: null,
      opex_annual: 1200,
    },
    vendor: {
      type: "Developer",
      urgency: "Normal",
      exclusivity: "Open",
      motivation: "Inventory Clear",
    },
    meta: {
      project: "Horizon",
      floor: 3,
      view: "Sea (Panoramic)",
      dist_sea: 400,
      legal: "Off-Plan",
    },
    fit_score: { "Coastal Yield Hunter": 95, "Mountain Investor": 10 },
    days_on_market: 22,
  },
  {
    id: "P-103",
    title: "Kolasin Ski Chalet B4",
    city: "Kolasin",
    sub: "Ski Resort 1450",
    price: 350000,
    size: 95,
    type: "Townhouse",
    status: "Under Construction",
    tags: ["ski-in", "nature"],
    performance: {
      yield_realized: null,
      yield_est: 6.5,
      occupancy: null,
      opex_annual: 2500,
    },
    vendor: {
      type: "Developer",
      urgency: "High",
      exclusivity: "Partner",
      motivation: "Pre-sales",
    },
    meta: {
      project: "Grand Chalet",
      floor: 0,
      view: "Mountain",
      dist_sea: 0,
      legal: "Clean",
    },
    fit_score: { "Mountain Investor": 99, "Lifestyle Expat": 40 },
    days_on_market: 45,
  },
  {
    id: "P-104",
    title: "Unesco Stone Villa Restoration",
    city: "Kotor",
    sub: "Dobrota",
    price: 920000,
    size: 180,
    type: "House",
    status: "Resale",
    tags: ["historic", "character"],
    performance: { yield_realized: 3.1, occupancy: 60, opex_annual: 8000 },
    vendor: {
      type: "Private",
      urgency: "Distressed",
      exclusivity: "Exclusive",
      motivation: "Relocation",
    },
    meta: {
      project: null,
      floor: null,
      view: "Bay",
      dist_sea: 20,
      legal: "Pending",
    },
    fit_score: { "Lifestyle Expat": 85, "The Villa Collector": 30 },
    days_on_market: 210,
  },
];

const PERSONAS = [
  {
    key: "CoastalYieldHunter",
    name: "Coastal Yield Hunter",
    color: "brand",
    icon: Waves,
  },
  {
    key: "VillaCollector",
    name: "The Villa Collector",
    color: "purple",
    icon: DollarSign,
  },
  {
    key: "LifestyleExpat",
    name: "Lifestyle Expat",
    color: "success",
    icon: Home,
  },
  {
    key: "MountainInvestor",
    name: "Mountain Investor",
    color: "warning",
    icon: Mountain,
  },
];

const CAPITAL_PIPELINES = [
  {
    id: "turkey",
    label: "Turkey",
    flag: "🇹🇷",
    volume_level: "High",
    urgency_level: "High",
    avg_ticket_eur: 450000,
    corePositioning: "EU-adjacent lifestyle, safe harbour for capital.",
    personas: [
      {
        key: "CoastalYieldHunter",
        shareLeads: 70,
        shareRev: 50,
        challenges: ["Currency volatility", "Trust in delivery"],
      },
      {
        key: "LifestyleExpat",
        shareLeads: 20,
        shareRev: 30,
        challenges: ["Residency path", "Schooling"],
      },
      {
        key: "VillaCollector",
        shareLeads: 10,
        shareRev: 20,
        challenges: ["Finding trophy stock", "Privacy"],
      },
    ],
    filter_logic: (p) =>
      (p.performance.yield_est > 6 || p.performance.yield_realized > 6) &&
      (p.status === "New Construction" || p.status === "Under Construction"),
    heroOffers: [
      "Curated 3-unit portfolio pack (Budva/Becici)",
      "Move-in-ready units with rental guarantee",
    ],
    immediateActions: [
      "Launch Turkish-language webinar on 'Yield + Passport'",
      "Retarget Istanbul portal traffic",
      "WhatsApp portfolio drops",
    ],
    channelStack: [
      { label: "Local Partners", share: 45, conv: "High" },
      { label: "Digital (Yield Ads)", share: 30, conv: "Med" },
      { label: "Webinars", share: 15, conv: "Med" },
    ],
    complianceRisks: [
      "Financing difficult (Cash buyers)",
      "Need clear residency structure",
    ],
    targets: {
      intents: ["Passport", "Yield", "Currency Hedge"],
      regions: ["Budva", "Becici", "Tivat"],
    },
  },
  {
    id: "israel",
    label: "Israel",
    flag: "🇮🇱",
    volume_level: "Med",
    urgency_level: "Med",
    avg_ticket_eur: 320000,
    corePositioning: "Dual-season stability close to home.",
    personas: [
      {
        key: "MountainInvestor",
        shareLeads: 50,
        shareRev: 40,
        challenges: ["Understanding new markets", "Occupancy confidence"],
      },
      {
        key: "LifestyleExpat",
        shareLeads: 35,
        shareRev: 45,
        challenges: ["Community questions", "Comparing to Cyprus"],
      },
    ],
    filter_logic: (p) =>
      p.city === "Kolasin" || (p.city === "Tivat" && p.price < 600000),
    heroOffers: ["Ski-in/Ski-out chalets with full management"],
    immediateActions: [
      "Roadshow in Tel Aviv (Winter Focus)",
      "Partner with family offices",
    ],
    channelStack: [
      { label: "Wealth Managers", share: 60, conv: "High" },
      { label: "Direct Search", share: 20, conv: "Low" },
      { label: "Community Events", share: 20, conv: "Med" },
    ],
    complianceRisks: [
      "High expectations for management",
      "Tax structuring questions",
    ],
    targets: {
      intents: ["Diversification", "Second Home"],
      regions: ["Kolasin", "Tivat"],
    },
  },
  {
    id: "russia",
    label: "Russia",
    flag: "🇷🇺",
    volume_level: "Low",
    urgency_level: "Low",
    avg_ticket_eur: 1500000,
    corePositioning: "Unmatched privacy and Adriatic legacy.",
    personas: [
      {
        key: "VillaCollector",
        shareLeads: 80,
        shareRev: 90,
        challenges: ["Sanctions / Banking", "Privacy"],
      },
    ],
    filter_logic: (p) => p.price > 1000000 && p.tags.includes("waterfront"),
    heroOffers: ["Off-market waterfront estates (White-glove)"],
    immediateActions: [
      "Direct 1:1 outreach via legal partners",
      "Exclusive viewing trips",
    ],
    channelStack: [
      { label: "Private Network", share: 80, conv: "High" },
      { label: "Luxury Portals", share: 10, conv: "Low" },
    ],
    complianceRisks: ["KYC / Sanctions checks essential", "Payment logistics"],
    targets: {
      intents: ["Elite Coastal", "Asset Protection"],
      regions: ["Tivat", "Lustica Bay"],
    },
  },
  {
    id: "ukraine",
    label: "Ukraine",
    flag: "🇺🇦",
    volume_level: "Med",
    urgency_level: "High",
    avg_ticket_eur: 280000,
    corePositioning: "Immediate stability in a culturally compatible region.",
    personas: [
      {
        key: "LifestyleExpat",
        shareLeads: 70,
        shareRev: 60,
        challenges: ["Cash liquidity", "Urgent timeline"],
      },
    ],
    filter_logic: (p) => p.status === "Resale" && p.type !== "Land",
    heroOffers: ["Move-in ready apartments with residency support"],
    immediateActions: [
      "Telegram community management",
      "Residency legal support bundles",
    ],
    channelStack: [
      { label: "Telegram Groups", share: 50, conv: "Med" },
      { label: "Referrals", share: 30, conv: "High" },
    ],
    complianceRisks: ["Source of funds checks", "Urgent documentation"],
    targets: {
      intents: ["Relocation", "Immediate Housing"],
      regions: ["Bar", "Budva"],
    },
  },
];

const REGIONS = [
  {
    city: "Tivat",
    type: "Luxury",
    avg_yield: "4.8%",
    micro: { airport: "5m", school: true, season: "Year-round", infra: "High" },
  },
  {
    city: "Budva",
    type: "Volume",
    avg_yield: "7.2%",
    micro: { airport: "35m", school: false, season: "Summer", infra: "Med" },
  },
  {
    city: "Kotor",
    type: "Historic",
    avg_yield: "5.1%",
    micro: { airport: "15m", school: true, season: "Dual", infra: "Low" },
  },
  {
    city: "Kolasin",
    type: "Resort",
    avg_yield: "6.5%",
    micro: {
      airport: "1h 10m",
      school: false,
      season: "Winter/Summer",
      infra: "Rising",
    },
  },
];

const DRIVERS = [
  {
    feature: "Price / m² < €2,200",
    impact: "+45%",
    metric: "Sales Velocity",
    category: "Pricing",
  },
  {
    feature: "Sea View (First Line)",
    impact: "+3.2pp",
    metric: "Net Yield",
    category: "Asset Quality",
  },
  {
    feature: "Clean Legal Title",
    impact: "+60%",
    metric: "Conversion Rate",
    category: "Risk",
  },
];

// --- COMPONENT PRIMITIVES ---

const Badge = ({ text, tone }) => (
  <span style={commonStyles.badge(tone)}>{text}</span>
);

const StatCard = ({ title, value, sub, trend, alert }) => (
  <div style={commonStyles.card}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 16,
      }}
    >
      <div style={{ fontSize: 13, color: palette.textSoft, fontWeight: 500 }}>
        {title}
      </div>
      {trend && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: trend > 0 ? palette.success : palette.danger,
            fontWeight: 600,
          }}
        >
          {trend > 0 ? "+" : ""}
          {trend}% <TrendingUp size={14} />
        </div>
      )}
    </div>
    <div
      style={{
        fontSize: 28,
        fontWeight: 700,
        color: palette.text,
        letterSpacing: "-0.02em",
      }}
    >
      {value}
    </div>
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}
    >
      {alert && <AlertCircle size={14} color={palette.warning} />}
      <div style={{ fontSize: 13, color: palette.textFaint }}>{sub}</div>
    </div>
  </div>
);

// --- MODULES ---

const MarketOverview = () => (
  <div>
    <div style={commonStyles.sectionHeader}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: palette.text }}>
          Market Intelligence
        </h1>
        <p style={{ color: palette.textSoft, marginTop: 4 }}>
          Real-time valuation, drivers analysis, and predictive signals.
        </p>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            background: palette.brand,
            border: "none",
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
          }}
        >
          Export Brief
        </button>
      </div>
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 24,
        marginBottom: 32,
      }}
    >
      <StatCard
        title="Total Asset Value"
        value="€482.5M"
        sub="Weighted Portfolio"
        trend={12.4}
      />
      <StatCard
        title="Avg. Absorption Rate"
        value="3.2 mo"
        sub="Time to Liquidity"
        trend={-5.1}
      />
      <StatCard
        title="Active Demand"
        value="€145M"
        sub="Qualified Lead Budget"
        trend={8.2}
      />
      <StatCard
        title="Yield Spread"
        value="3.5% - 8.2%"
        sub="ROI Variance"
        alert
      />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
      <div style={commonStyles.card}>
        <div style={commonStyles.h2}>
          <Zap size={18} color={palette.brand} /> Alpha Drivers
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {DRIVERS.map((d, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px",
                border: `1px solid ${palette.glassBorder}`,
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: palette.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 12,
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: palette.brand,
                  }}
                >
                  {i + 1}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{ fontWeight: 600, fontSize: 14, color: palette.text }}
                >
                  {d.feature}
                </div>
                <div style={{ fontSize: 12, color: palette.textSoft }}>
                  Category: {d.category}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: d.impact.includes("+")
                      ? palette.success
                      : palette.danger,
                  }}
                >
                  {d.impact}
                </div>
                <div style={{ fontSize: 11, color: palette.textFaint }}>
                  on {d.metric}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={commonStyles.card}>
        <div style={commonStyles.h2}>
          <TrendingUp size={18} color={palette.warning} /> Market Signals
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[
            {
              text: "Tivat Luxury Inventory critical low (<15 units)",
              impact: "High",
              color: "danger",
            },
            {
              text: "Budva Studio yields compressing to 5.5%",
              impact: "Med",
              color: "warning",
            },
            {
              text: "Surge in Turkish buyer inquiries (+40% MoM)",
              impact: "High",
              color: "success",
            },
          ].map((signal, i) => (
            <div
              key={i}
              style={{
                padding: 12,
                borderRadius: 8,
                background: palette.bg,
                borderLeft: `3px solid ${palette[signal.color]}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                {signal.text}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: palette.textFaint,
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                Impact: {signal.impact}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const RegionalAnalysis = () => (
  <div>
    <div style={commonStyles.sectionHeader}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: palette.text }}>
        Regional Performance
      </h1>
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 24,
      }}
    >
      {REGIONS.map((r, i) => (
        <div key={i} style={commonStyles.card}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 16,
            }}
          >
            <div>
              <h3
                style={{ fontSize: 18, fontWeight: 700, color: palette.text }}
              >
                {r.city}
              </h3>
              <div style={{ fontSize: 13, color: palette.textSoft }}>
                Type: {r.type}
              </div>
            </div>
            <Badge text={`Yield: ${r.avg_yield}`} tone="success" />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              fontSize: 13,
            }}
          >
            <div
              style={{ padding: 8, background: palette.bg, borderRadius: 6 }}
            >
              <span style={{ color: palette.textFaint, fontSize: 11 }}>
                Seasonality
              </span>
              <div style={{ fontWeight: 600 }}>{r.micro.season}</div>
            </div>
            <div
              style={{ padding: 8, background: palette.bg, borderRadius: 6 }}
            >
              <span style={{ color: palette.textFaint, fontSize: 11 }}>
                Infrastructure
              </span>
              <div style={{ fontWeight: 600 }}>{r.micro.infra}</div>
            </div>
            <div
              style={{ padding: 8, background: palette.bg, borderRadius: 6 }}
            >
              <span style={{ color: palette.textFaint, fontSize: 11 }}>
                Airport Prox.
              </span>
              <div style={{ fontWeight: 600 }}>{r.micro.airport}</div>
            </div>
            <div
              style={{ padding: 8, background: palette.bg, borderRadius: 6 }}
            >
              <span style={{ color: palette.textFaint, fontSize: 11 }}>
                Intl. School
              </span>
              <div style={{ fontWeight: 600 }}>
                {r.micro.school ? "Yes" : "No"}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const InventoryDeepDive = () => (
  <div>
    <div style={commonStyles.sectionHeader}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: palette.text }}>
        Asset Matrix
      </h1>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
      {PROPERTIES.map((p) => (
        <div
          key={p.id}
          style={{
            ...commonStyles.card,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                color: palette.brand,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {p.city} • {p.sub}
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: palette.text,
                marginTop: 4,
              }}
            >
              {p.title}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {p.tags.map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: 11,
                    background: palette.bg,
                    padding: "2px 8px",
                    borderRadius: 4,
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>
              €{p.price.toLocaleString()}
            </div>
            <div style={{ fontSize: 13, color: palette.textSoft }}>
              {p.size}m² • {p.type}
            </div>
            <div style={{ marginTop: 4 }}>
              <Badge
                text={p.status}
                tone={p.status === "Resale" ? "brand" : "success"}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const StrategyCommand = () => {
  const [activePipelineId, setActivePipelineId] = useState(null);
  const activePipeline = CAPITAL_PIPELINES.find(
    (p) => p.id === activePipelineId
  );
  const getMatchingInventoryCount = (logicFn) =>
    PROPERTIES.filter(logicFn).length;

  if (activePipeline) {
    const matchingCount = getMatchingInventoryCount(
      activePipeline.filter_logic
    );
    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <button
            onClick={() => setActivePipelineId(null)}
            style={{
              background: "transparent",
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: palette.textSoft,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <ChevronLeft size={16} /> Back to Command
          </button>
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}
        >
          {/* LEFT: DETAILS */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div style={commonStyles.card}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ display: "flex", gap: 16 }}>
                  <div style={{ fontSize: 48 }}>{activePipeline.flag}</div>
                  <div>
                    <h1
                      style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: palette.text,
                        marginBottom: 4,
                      }}
                    >
                      {activePipeline.label} Pipeline
                    </h1>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Badge
                        text={`Avg Ticket: €${(
                          activePipeline.avg_ticket_eur / 1000
                        ).toFixed(0)}k`}
                        tone="default"
                      />
                      <Badge
                        text={`Vol: ${activePipeline.volume_level}`}
                        tone={
                          activePipeline.volume_level === "High"
                            ? "success"
                            : "default"
                        }
                      />
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: palette.textFaint,
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Urgency
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color:
                        activePipeline.urgency_level === "High"
                          ? palette.danger
                          : palette.text,
                    }}
                  >
                    {activePipeline.urgency_level}
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: palette.bg,
                  borderRadius: 8,
                  borderLeft: `4px solid ${palette.brand}`,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: palette.brand,
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  Core Positioning
                </div>
                <div
                  style={{ fontSize: 14, fontWeight: 500, fontStyle: "italic" }}
                >
                  "{activePipeline.corePositioning}"
                </div>
              </div>
              <div style={{ marginTop: 24 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: palette.textFaint,
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Buyer Persona Mix
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  {activePipeline.personas.map((p, i) => {
                    const personaDetails = PERSONAS.find(
                      (gp) => gp.key === p.key
                    );
                    return (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          padding: 12,
                          borderRadius: 8,
                          border: `1px solid ${palette.glassBorder}`,
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              padding: 4,
                              borderRadius: 4,
                              background: palette.bg,
                            }}
                          >
                            {personaDetails && (
                              <personaDetails.icon
                                size={14}
                                color={palette[personaDetails.color]}
                              />
                            )}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>
                            {personaDetails ? personaDetails.name : p.key}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 11,
                            marginBottom: 4,
                          }}
                        >
                          <span style={{ color: palette.textSoft }}>
                            Leads:
                          </span>
                          <span style={{ fontWeight: 700 }}>
                            {p.shareLeads}%
                          </span>
                        </div>
                        <div
                          style={{
                            width: "100%",
                            height: 4,
                            background: palette.bg,
                            borderRadius: 2,
                          }}
                        >
                          <div
                            style={{
                              width: `${p.shareLeads}%`,
                              height: "100%",
                              background:
                                palette[personaDetails?.color || "brand"],
                              borderRadius: 2,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 10,
                            color: palette.textFaint,
                          }}
                        >
                          Challenge: {p.challenges[0]}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={commonStyles.card}>
              <div style={commonStyles.h2}>
                <FileText size={18} color={palette.text} /> Execution Playbook
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 24,
                  marginTop: 16,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: palette.textFaint,
                      marginBottom: 8,
                      textTransform: "uppercase",
                    }}
                  >
                    Hero Offers
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {activePipeline.heroOffers.map((offer, i) => (
                      <div
                        key={i}
                        style={{
                          padding: 12,
                          background: palette.brandGlow,
                          borderRadius: 8,
                          color: "#92400E",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {offer}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: palette.textFaint,
                      marginBottom: 8,
                      textTransform: "uppercase",
                    }}
                  >
                    Immediate Actions
                  </div>
                  <ul style={{ paddingLeft: 16, margin: 0 }}>
                    {activePipeline.immediateActions.map((action, i) => (
                      <li
                        key={i}
                        style={{
                          fontSize: 13,
                          color: palette.textSoft,
                          marginBottom: 6,
                        }}
                      >
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div style={commonStyles.card}>
              <div style={commonStyles.h2}>
                <Megaphone size={18} color={palette.text} /> Channel Stack
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  marginTop: 12,
                }}
              >
                {activePipeline.channelStack.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom:
                        i < activePipeline.channelStack.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {c.label}
                    </div>
                    <div style={{ display: "flex", gap: 24, fontSize: 13 }}>
                      <div style={{ width: 100 }}>
                        <span
                          style={{
                            fontSize: 11,
                            color: palette.textFaint,
                            marginRight: 6,
                          }}
                        >
                          Share
                        </span>
                        <span style={{ fontWeight: 600 }}>{c.share}%</span>
                      </div>
                      <div style={{ width: 100 }}>
                        <span
                          style={{
                            fontSize: 11,
                            color: palette.textFaint,
                            marginRight: 6,
                          }}
                        >
                          Conv
                        </span>
                        <Badge
                          text={c.conv}
                          tone={
                            c.conv === "High"
                              ? "success"
                              : c.conv === "Low"
                              ? "danger"
                              : "default"
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: INVENTORY LENS */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div
              style={{
                ...commonStyles.card,
                background: palette.text,
                color: "#fff",
                border: "none",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: 0.8,
                  marginBottom: 8,
                  textTransform: "uppercase",
                }}
              >
                Inventory Match
              </div>
              <div style={{ fontSize: 48, fontWeight: 700, marginBottom: 4 }}>
                {matchingCount}
              </div>
              <div style={{ fontSize: 14, opacity: 0.9 }}>
                Units fit pipeline criteria
              </div>
              <button
                style={{
                  marginTop: 24,
                  width: "100%",
                  padding: "12px",
                  background: palette.brand,
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                View Matching Stock
              </button>
            </div>

            <div style={commonStyles.card}>
              <div style={commonStyles.h2}>
                <Target size={18} color={palette.brand} /> Targets
              </div>
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: palette.textFaint,
                    textTransform: "uppercase",
                  }}
                >
                  Primary Intents
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    marginTop: 6,
                  }}
                >
                  {activePipeline.targets.intents.map((t) => (
                    <Badge key={t} text={t} tone="brand" />
                  ))}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: palette.textFaint,
                    textTransform: "uppercase",
                  }}
                >
                  Fav Regions
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    marginTop: 6,
                  }}
                >
                  {activePipeline.targets.regions.map((t) => (
                    <Badge key={t} text={t} tone="default" />
                  ))}
                </div>
              </div>
            </div>

            <div style={commonStyles.card}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 12,
                  color: palette.danger,
                }}
              >
                <ShieldAlert size={18} />
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                  Compliance & Risks
                </span>
              </div>
              <ul style={{ paddingLeft: 16, margin: 0 }}>
                {activePipeline.complianceRisks.map((c, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: 12,
                      color: palette.textSoft,
                      marginBottom: 6,
                    }}
                  >
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // OVERVIEW VIEW
  return (
    <div>
      <div style={commonStyles.sectionHeader}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: palette.text }}>
            Target Strategy Command
          </h1>
          <p style={{ color: palette.textSoft, marginTop: 4 }}>
            Direct engagement channels for Luxe Realty's core audiences.
          </p>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          marginBottom: 32,
        }}
      >
        <div style={commonStyles.card}>
          <div style={commonStyles.h2}>
            <TrendingUp size={20} color={palette.success} /> Investment Grade
          </div>
          <p
            style={{ fontSize: 13, color: palette.textSoft, marginBottom: 16 }}
          >
            Targeting "Coastal Yield Hunters" & Commercial Buyers.
          </p>
          <div style={{ background: palette.bg, padding: 16, borderRadius: 8 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600 }}>
                Active Yield Inventory
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: palette.success,
                }}
              >
                42 Units
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Badge text="ROI > 6%" tone="success" />
              <Badge text="Off-Plan" tone="brand" />
            </div>
          </div>
        </div>
        <div style={commonStyles.card}>
          <div style={commonStyles.h2}>
            <Gem size={20} color={palette.brand} /> The Luxe Standard
          </div>
          <p
            style={{ fontSize: 13, color: palette.textSoft, marginBottom: 16 }}
          >
            "Turquoise waters, dramatic views, and boats."
          </p>
          <div style={{ background: palette.bg, padding: 16, borderRadius: 8 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600 }}>
                Trophy Assets
              </span>
              <span
                style={{ fontSize: 12, fontWeight: 700, color: palette.brand }}
              >
                12 Listings
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Badge text="Sea View" tone="brand" />
              <Badge text="Privacy" tone="default" />
            </div>
          </div>
        </div>
      </div>
      <div>
        <h2
          style={{
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 16,
            color: palette.text,
          }}
        >
          Global Capital Pipelines
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 16,
          }}
        >
          {CAPITAL_PIPELINES.map((m) => (
            <div
              key={m.id}
              onClick={() => setActivePipelineId(m.id)}
              style={{
                padding: 20,
                borderRadius: 12,
                border: `1px solid ${palette.glassBorder}`,
                background: palette.bg,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 32 }}>{m.flag}</div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color:
                      m.volume_level === "High"
                        ? palette.success
                        : palette.textFaint,
                  }}
                >
                  Vol: {m.volume_level}
                </div>
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: palette.text,
                  marginBottom: 2,
                }}
              >
                {m.label}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: palette.textFaint,
                  marginBottom: 12,
                }}
              >
                Avg. €{(m.avg_ticket_eur / 1000).toFixed(0)}k
              </div>
              <div
                style={{
                  paddingTop: 12,
                  borderTop: `1px solid ${palette.glassBorder}`,
                  fontSize: 11,
                  fontWeight: 600,
                  color: palette.brand,
                }}
              >
                {m.targets.intents[0]} & {m.targets.intents[1]}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BuyerPersonas = () => (
  <div>
    <div style={commonStyles.sectionHeader}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: palette.text }}>
        Buyer Demand Segments
      </h1>
      <p style={{ color: palette.textSoft, marginTop: 4 }}>
        Quantitative profile analysis: Lead volume vs. Revenue potential.
      </p>
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 24,
      }}
    >
      {PERSONAS.map((p, index) => (
        <div key={index} style={commonStyles.card}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", gap: 16 }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 12,
                  background: palette.brandGlow,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <p.icon size={28} color={palette.brand} />
              </div>
              <div>
                <h3
                  style={{ fontSize: 18, fontWeight: 700, color: palette.text }}
                >
                  {p.name}
                </h3>
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <Badge text="Active Segment" tone="default" />
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              fontSize: 13,
              color: palette.textSoft,
              lineHeight: 1.5,
              marginBottom: 16,
            }}
          >
            Global demand segment. See Pipeline Details for specific country
            breakdowns.
          </div>
        </div>
      ))}
    </div>
  </div>
);

// --- MAIN APP COMPONENT ---

export default function App() {
  const [tab, setTab] = useState("overview");

  const NAV = [
    { id: "overview", label: "Intelligence", icon: LayoutGrid },
    { id: "regional", label: "Regional Perf.", icon: MapIcon },
    { id: "strategy", label: "Target Strategy", icon: Plane },
    { id: "inventory", label: "Asset Matrix", icon: Building },
    { id: "personas", label: "Demand Segments", icon: Users },
  ];

  const renderContent = () => {
    switch (tab) {
      case "overview":
        return <MarketOverview />;
      case "regional":
        return <RegionalAnalysis />;
      case "strategy":
        return <StrategyCommand />;
      case "inventory":
        return <InventoryDeepDive />;
      case "personas":
        return <BuyerPersonas />;
      default:
        return <MarketOverview />;
    }
  };

  return (
    <div style={commonStyles.container}>
      <aside style={commonStyles.sidebar}>
        <div style={{ marginBottom: "40px" }}>
          <div
            style={{
              fontSize: 10,
              color: palette.brand,
              textTransform: "uppercase",
              letterSpacing: "0.2em",
              marginBottom: 8,
              fontWeight: 800,
            }}
          >
            GTM ENGINE v4.0
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 32,
                height: 32,
                background: palette.text,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: palette.brand,
                fontWeight: 700,
                fontSize: 18,
              }}
            >
              M
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: palette.text,
                letterSpacing: "-0.03em",
              }}
            >
              Montenegro<span style={{ color: palette.brand }}>Est.</span>
            </div>
          </div>
        </div>
        <nav
          style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}
        >
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              style={commonStyles.navItem(tab === n.id)}
            >
              <n.icon size={18} />
              <span>{n.label}</span>
            </button>
          ))}
        </nav>
        <div
          style={{
            marginTop: "auto",
            padding: 20,
            background: "#F3F4F6",
            borderRadius: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                background: palette.success,
                borderRadius: "50%",
                boxShadow: `0 0 8px ${palette.success}`,
              }}
            />
            <span
              style={{ fontSize: 12, fontWeight: 600, color: palette.text }}
            >
              System Active
            </span>
          </div>
          <div style={{ fontSize: 11, color: palette.textSoft }}>
            Processing 1,240 Assets...
          </div>
        </div>
      </aside>
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "32px 40px",
          gap: 24,
          overflowY: "hidden",
        }}
      >
        <section style={commonStyles.glassPanel}>{renderContent()}</section>
      </main>
    </div>
  );
}
